import DetailsIcon from '@mui/icons-material/Info';
import DashboardIcon from '@mui/icons-material/InsertChart';
import FeesIcon from '@mui/icons-material/LocalOffer';
import AdminsIcon from '@mui/icons-material/ManageAccounts';
import PaymentIcon from '@mui/icons-material/Payment';
import { Badge, Box, Stack } from '@mui/material';
import { FC } from 'react';
import {
  ImageField,
  NumberField,
  ReferenceManyField,
  TextField,
  useTranslate,
  WithRecord,
} from 'react-admin';

import { isBillingAddressDifferent, isMailingAddressDifferent } from '@Common/helpers/address';
import {
  GetContractFeeChargesEndpoint,
  getB2bContractFeeChargesSortColumnValues,
} from '@Common/types/bo/b2b/contract_fee_charges';
import { GetCompanyResponse } from '@Common/types/bo_external/companies';

import { b2bCompanyLegalFormChoices } from '@sh4res/bo-common/choices/companies';
import {
  B2bCompanyDashboard,
  DatagridTyped,
  Pagination,
  ShowFields,
  ShowSection,
  ShowWithNavigation,
  TypographyTranslated,
  VerticalTab,
  VerticalTabsLayout,
} from '@sh4res/bo-common/components';
import { B2bAdminCreate } from '@sh4res/bo-common/components/B2bAdmins/B2bAdminCreate';
import { B2bAdminsList } from '@sh4res/bo-common/components/B2bAdmins/B2bAdminsList';
import { useB2bCompanyDashboardPermissions } from '@sh4res/bo-common/components/B2bCompanyDashboard/B2bCompanyDashboard';
import { B2bCompaniesPaymentMethodCreate } from '@sh4res/bo-common/components/B2bCompanyPaymentMethods/company_payment_methods/B2bCompanyPaymentMethodCreate';
import { B2bCompanyPaymentMethodsList } from '@sh4res/bo-common/components/B2bCompanyPaymentMethods/company_payment_methods/B2bCompanyPaymentMethodsList';
import { ChipFieldsList, SelectChipField, UTCDateField } from '@sh4res/bo-common/fields';
import { useCanAccess } from '@sh4res/bo-common/hooks';
import { useB2bCompanyIdGuard } from '@sh4res/bo-common/resources/investor_profile/hooks/useB2bCompanyIdGuard';
import { shouldMockDashboardData } from '@sh4res/bo-common/utils/should_mock_dashboard_data';

import { GhostLinkField } from '@Resources/admins/GhostLinkField';
import { ContractFeeChargesListColumns } from '@Resources/contract_fee_charges/ContractFeeChargesList';

export const CompaniesShow: FC = () => {
  const testId = 'BoExternalCompaniesShow';
  const canAccessCompany = useCanAccess({
    resource: 'companies',
    action: 'show',
  });
  const canAccessContractFeeCharges = useCanAccess({
    resource: 'contract_fee_charges',
    action: 'list',
  });
  const canAccessAdmins = useCanAccess({
    resource: 'admins',
    action: 'list',
  });
  const canAccessDedicatedDashboard = useCanAccess({
    resource: 'dashboard',
    action: 'show',
  });
  const canListPaymentMethods = useCanAccess({
    resource: 'companies/payment_methods',
    action: 'list',
  });

  const { hasAnyDashboardAccess } = useB2bCompanyDashboardPermissions('companies');

  const translate = useTranslate();

  const { companyId } = useB2bCompanyIdGuard({ shouldRedirect: true });

  if (companyId === null || !canAccessCompany) {
    return null;
  }

  const shouldShowPaymentMethodsDot = (record: GetCompanyResponse) => !record.has_payment_methods;

  return (
    <ShowWithNavigation
      actions={false}
      avatar={{ name_source: 'name', image_source: 'logo_url' }}
      title={<WithRecord render={(record: GetCompanyResponse) => record.name} />}
      sx={{ height: '100%' }}
      data-tour-id="company-show-view">
      <WithRecord
        render={(record: GetCompanyResponse) => (
          <VerticalTabsLayout data-tour-id="company-show-view-tabs">
            {/* Do not display company dashboard if user can access dedicated dashboard view */}
            {!canAccessDedicatedDashboard && hasAnyDashboardAccess && (
              <VerticalTab
                icon={<DashboardIcon style={{ width: '20px', height: '20px' }} />}
                label="resources.companies.tabs.dashboard.label"
                data-testid="BoExternalCompanyShow_DashboardVerticalTab"
                sx={{ textTransform: 'none' }}
                path="dashboard">
                <WithRecord
                  render={(record) => (
                    <B2bCompanyDashboard
                      resource="companies"
                      useMock={shouldMockDashboardData(record.id)}
                    />
                  )}
                />
              </VerticalTab>
            )}
            <VerticalTab
              icon={<DetailsIcon style={{ width: '20px', height: '20px' }} />}
              label="resources.companies.tabs.details.label"
              path="details">
              <ShowSection label="general.sections.details">
                <ShowFields<GetCompanyResponse>
                  fields={{
                    name: <TextField data-testid={`${testId}_NameField`} />,
                    siret: <TextField data-testid={`${testId}_SiretField`} />,
                    international_identifier: (
                      <TextField data-testid={`${testId}_InternationalIdentifierField`} />
                    ),
                    legal_form: (
                      <SelectChipField
                        choices={b2bCompanyLegalFormChoices}
                        data-testid={`${testId}_LegalFormField`}
                      />
                    ),
                    logo_url: (
                      <ImageField
                        title={translate('resources.companies.fields.logo_url')}
                        data-testid={`${testId}_LogoUrlField`}
                      />
                    ),
                    registration_date: (
                      <UTCDateField data-testid={`${testId}_RegistrationDateField`} />
                    ),
                    rcs_location: <TextField data-testid={`${testId}_RcsLocationField`} />,
                    naf_code: <TextField data-testid={`${testId}_NafCodeField`} />,
                    previous_naf_code: <TextField data-testid={`${testId}_PreviousNafCodeField`} />,
                    headcount: <NumberField data-testid={`${testId}_HeadcountField`} />,
                    collective_agreement_code: (
                      <TextField data-testid={`${testId}_CollectiveAgreementCodeField`} />
                    ),
                    collective_agreement_name: (
                      <TextField data-testid={`${testId}_CollectiveAgreementNameField`} />
                    ),
                    retirement_age: <NumberField data-testid={`${testId}_RetirementAgeField`} />,
                    domains: <ChipFieldsList source="" />,
                  }}
                />
              </ShowSection>
              <ShowSection label="resources.companies.tabs.details.headers.financial_information">
                <ShowFields<GetCompanyResponse>
                  fields={{
                    start_fiscal_year: <TextField data-testid={`${testId}_StartFiscalYearField`} />,
                  }}
                />
              </ShowSection>
              <ShowSection label="resources.companies.tabs.details.headers.address">
                <ShowFields<GetCompanyResponse>
                  fields={{
                    street: <TextField data-testid={`${testId}_AddressStreetField`} />,
                    postalcode: <TextField data-testid={`${testId}_AddressPostalCodeField`} />,
                    city: <TextField data-testid={`${testId}_AddressCityField`} />,
                    country: <TextField data-testid={`${testId}_AddressCountryField`} />,
                  }}
                />
              </ShowSection>
              <ShowSection label="resources.companies.tabs.details.headers.mailing_address">
                <ShowFields<GetCompanyResponse>
                  fields={
                    isMailingAddressDifferent(record)
                      ? {
                          mailing_street: (
                            <TextField data-testid={`${testId}_MailingStreetField`} />
                          ),
                          mailing_postalcode: (
                            <TextField data-testid={`${testId}_MailingPostalCodeField`} />
                          ),
                          mailing_city: <TextField data-testid={`${testId}_MailingCityField`} />,
                          mailing_country: (
                            <TextField data-testid={`${testId}_MailingCountryField`} />
                          ),
                        }
                      : {
                          street: (
                            <TextField data-testid={`${testId}_MailingFallbackStreetField`} />
                          ),
                          postalcode: (
                            <TextField data-testid={`${testId}_MailingFallbackPostalCodeField`} />
                          ),
                          city: <TextField data-testid={`${testId}_MailingFallbackCityField`} />,
                          country: (
                            <TextField data-testid={`${testId}_MailingFallbackCountryField`} />
                          ),
                        }
                  }
                />
              </ShowSection>
              <ShowSection label="resources.companies.tabs.details.headers.billing_address">
                <ShowFields<GetCompanyResponse>
                  fields={
                    isBillingAddressDifferent(record)
                      ? {
                          billing_street: (
                            <TextField data-testid={`${testId}_BillingStreetField`} />
                          ),
                          billing_postalcode: (
                            <TextField data-testid={`${testId}_BillingPostalCodeField`} />
                          ),
                          billing_city: <TextField data-testid={`${testId}_BillingCityField`} />,
                          billing_country: (
                            <TextField data-testid={`${testId}_BillingCountryField`} />
                          ),
                        }
                      : {
                          street: (
                            <TextField data-testid={`${testId}_BillingFallbackStreetField`} />
                          ),
                          postalcode: (
                            <TextField data-testid={`${testId}_BillingFallbackPostalCodeField`} />
                          ),
                          city: <TextField data-testid={`${testId}_BillingFallbackCityField`} />,
                          country: (
                            <TextField data-testid={`${testId}_BillingFallbackCountryField`} />
                          ),
                        }
                  }
                />
              </ShowSection>
            </VerticalTab>
            {canAccessAdmins && (
              <VerticalTab
                icon={<AdminsIcon style={{ width: '20px', height: '20px' }} />}
                label="resources.companies.tabs.admins.label"
                path="admins"
                data-testid="BoExternalCompanyShow_AdminsVerticalTab"
                button={
                  <B2bAdminCreate
                    resource="admins"
                    label="resources.bo_common.b2b.admins.button"
                    record={{ company_id: record.id }}
                  />
                }>
                <B2bAdminsList
                  ghostLinkComponent={
                    <GhostLinkField data-testid={`${testId}_AdminsGhostLinkField`} />
                  }
                />
              </VerticalTab>
            )}
            {canAccessContractFeeCharges && (
              <VerticalTab
                icon={<FeesIcon style={{ width: '20px', height: '20px' }} />}
                label="resources.companies.tabs.fees.label"
                data-testid="BoExternalCompanyShow_FeesVerticalTab"
                path="fees">
                <Box sx={{ paddingTop: '3px', paddingBottom: '3px' }}>
                  <ReferenceManyField
                    reference="contract_fee_charges"
                    target="b2b_company_id"
                    perPage={10}
                    pagination={<Pagination handleFloatingButton={true} />}
                    data-testid={`${testId}_ContractFeeChargesField`}>
                    <DatagridTyped<GetContractFeeChargesEndpoint['Client']['ResponseBody'][number]>
                      rowClick="show"
                      bulkActionButtons={false}
                      showCard={true}
                      sort={{
                        field: 'created_at',
                        order: 'DESC',
                      }}
                      columnRegistry={ContractFeeChargesListColumns}
                      displayedColumns={[
                        'id',
                        'paid_by',
                        'b2b_contract_id',
                        'fee_label',
                        'amount',
                        'created_at',
                      ]}
                      sortableColumns={getB2bContractFeeChargesSortColumnValues}
                    />
                  </ReferenceManyField>
                </Box>
              </VerticalTab>
            )}
            {canListPaymentMethods && (
              <VerticalTab
                icon={
                  <Badge
                    variant="dot"
                    color="error"
                    invisible={!shouldShowPaymentMethodsDot(record)}>
                    <PaymentIcon />
                  </Badge>
                }
                label={
                  <Stack direction="row" spacing={0} alignItems="center">
                    <TypographyTranslated
                      label="resources.companies.tabs.payment_methods.label"
                      sx={{ margin: 0, fontWeight: 600 }}
                    />
                  </Stack>
                }
                path="payment_methods"
                button={
                  <B2bCompaniesPaymentMethodCreate
                    label="resources.bo_common.b2b.companies.payment_methods.button"
                    companyName={record.name}
                    record={{ company_id: record.id }}
                    hasCcb={record.has_ccb}
                  />
                }>
                <B2bCompanyPaymentMethodsList
                  source="id"
                  paginationProps={{ handleFloatingButton: true }}
                />
              </VerticalTab>
            )}
          </VerticalTabsLayout>
        )}
      />
    </ShowWithNavigation>
  );
};
